import type { PartProcess as SharedPartProcess } from '@misumi/shared'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import type { PartDetail } from './types'
import { problemTitle } from './usePartsLibrary'

/** Mirrors the generated `components["schemas"]["EditPartConfigRequest"]`
 * (`schema.gen.ts`, frozen by C0/ADR-0059 §1) — reused verbatim rather than
 * hand-typed, so this vertical never forks the request shape from the
 * contract it was generated from. */
export type EditPartConfigRequest = components['schemas']['EditPartConfigRequest']

/** `PartProcess` now DERIVES from `@misumi/shared`, which owns the single
 * declaration of `PART_PROCESSES` (ADR-0117 D2) — the generated manufacturing
 * catalog is keyed by it, and `apps/api` has to reach both, so the enum had to
 * move somewhere both apps can import. `PartProcessMatchesContract` below is
 * the anti-drift guard the collapse buys: it fails to COMPILE if the shared
 * tuple and the generated contract enum ever diverge in either direction, which
 * is strictly better than the three hand-kept copies silently disagreeing.
 */
export type PartProcess = SharedPartProcess
type MutuallyAssignable<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false
export type PartProcessMatchesContract = MutuallyAssignable<
  SharedPartProcess,
  components['schemas']['PartProcess']
> extends true
  ? true
  : never

export interface EditPartConfigFieldError {
  readonly field: string
  readonly detail: string
}

/** The 400 Problem+JSON body carries an `errors` extension member the
 * generated `Problem` schema doesn't type (C1/C2's structured field
 * errors) — this is that shape, read defensively at the one call site
 * below rather than widening the generated `Problem` type itself. */
interface ConfigInvalidProblem {
  readonly errors?: readonly EditPartConfigFieldError[]
}

export type EditPartConfigOutcome =
  | { readonly ok: true; readonly part: PartDetail }
  | {
      readonly ok: false
      readonly kind: 'field_errors'
      readonly errors: readonly EditPartConfigFieldError[]
    }
  | {
      readonly ok: false
      readonly kind: 'forbidden' | 'not_found' | 'error'
      readonly message: string
    }

export interface EditPartConfigService {
  editPartConfig(partId: string, request: EditPartConfigRequest): Promise<EditPartConfigOutcome>
}

/**
 * REAL BFF-backed save for `EditConfigurationModal` (UID-163/C3) — calls the
 * C2 `PATCH /parts/{partId}` handler and maps its four distinct outcomes
 * (200 refreshed PartDetail / 400 structured field errors / 403 capability-
 * denied / 404 tenant-scoped-no-leak) onto a discriminated result the modal
 * renders as TEXT (never color-only, WCAG 3.3.1) without ever branching on a
 * raw HTTP status itself.
 */
async function bffEditPartConfig(
  partId: string,
  request: EditPartConfigRequest,
): Promise<EditPartConfigOutcome> {
  const { data, error, response } = await apiClient.PATCH('/parts/{partId}', {
    params: { path: { partId } },
    body: request,
  })

  if (response.status === 400) {
    const body = error as ConfigInvalidProblem | undefined
    return { ok: false, kind: 'field_errors', errors: body?.errors ?? [] }
  }
  if (response.status === 403) {
    return {
      ok: false,
      kind: 'forbidden',
      message: "You don't have permission to edit this part's configuration.",
    }
  }
  if (response.status === 404) {
    return {
      ok: false,
      kind: 'not_found',
      message: 'This part could not be found. It may have been removed.',
    }
  }
  if (error || !data) {
    return {
      ok: false,
      kind: 'error',
      message: problemTitle(error, "We couldn't save this configuration. Try again."),
    }
  }
  return { ok: true, part: data as unknown as PartDetail }
}

export const defaultEditPartConfigService: EditPartConfigService = {
  editPartConfig: bffEditPartConfig,
}
